import { forwardRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Button } from 'components/Button';
import Icon from 'components/Icon/Icon';

const FADE_OUT_DURATION_MS = 500;
const ANIMATION_DURATION_MS = 800;

const bounceUp = keyframes`
    0%   { transform: rotate(0deg); }
    24%  { transform: rotate(25deg); }
    40%  { transform: rotate(-15deg); }
    60%  { transform: rotate(10deg); }
    80%  { transform: rotate(0deg); }
    100% { transform: rotate(0deg); }
`;

const bounceDown = keyframes`
    0%   { transform: rotate(0deg); }
    24%  { transform: rotate(15deg); }
    40%  { transform: rotate(-25deg); }
    60%  { transform: rotate(10deg); }
    80%  { transform: rotate(0deg); }
    100% { transform: rotate(0deg); }
`;

type StyledButtonWithIconProps = {
    $fadingOut: boolean;
};

const StyledButtonWithIcon = styled(Button.WithIcon)<StyledButtonWithIconProps>`
    color: ${({ theme }) => theme.ColorTextPrimary};
    transition: opacity ${FADE_OUT_DURATION_MS}ms ease;
    height: fit-content;
    padding: ${({ theme }) => theme.Spacing8};
    opacity: ${({ $fadingOut }) => ($fadingOut ? 0 : 1)};
`;

type FeedbackIconProps = {
    $feedbackGiven: boolean;
    $feedbackType: 'upvote' | 'downvote';
};

const FeedbackIcon = styled(Icon)<FeedbackIconProps>`
    fill: currentColor;
    transform-origin: ${({ $feedbackType }) => ($feedbackType === 'upvote' ? '15% 60%' : '85% 60%')};
    ${({ $feedbackGiven, $feedbackType }) => {
        return (
            $feedbackGiven &&
            css`
            animation: ${$feedbackType === 'upvote' ? bounceUp : bounceDown} ${ANIMATION_DURATION_MS}ms ease;
        `
        );
    }}
`;

type FeedbackButtonProps = {
    onGiveFeedback: (type: 'upvote' | 'downvote') => void;
    feedbackType: 'upvote' | 'downvote';
    feedbackGiven: boolean;
    fadingOut: boolean;
};

const FeedbackButton = forwardRef<HTMLButtonElement, FeedbackButtonProps>((props, ref) => {
    const { onGiveFeedback, feedbackType, feedbackGiven, fadingOut } = props;
    const [fadeOut, setFadeOut] = useState(false);

    const handleClick = () => {
        onGiveFeedback(feedbackType);

        setTimeout(() => {
            setFadeOut(true);
        }, ANIMATION_DURATION_MS);
    };

    return (
        <StyledButtonWithIcon
            variant="ghost"
            ref={ref}
            onClick={handleClick}
            $fadingOut={fadingOut || fadeOut}
            icon={
                <FeedbackIcon
                    $feedbackType={feedbackType}
                    $feedbackGiven={feedbackGiven}
                    type={feedbackType === 'upvote' ? 'ThumbsUp' : 'ThumbsDown'}
                    size={16}
                />
            }
        />
    );
});

export default FeedbackButton;
